// src/pages/enseignant/GestionFormations.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function GestionFormations() {
    const [formations, setFormations] = useState([]);
    const [inscrits, setInscrits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [showInscrits, setShowInscrits] = useState(false);
    const [selectedFormation, setSelectedFormation] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        nom: '',
        description: '',
        photo: '',
        frais_inscription: '',
        frais_formation: '',
        duree_mois: 3
    });
    const [inscritData, setInscritData] = useState({
        nom: '',
        prenom: '',
        date_naissance: '',
        lieu_naissance: '',
        sexe: 'M',
        telephone: '',
        email: '',
        montant_inscription: 0,
        montant_formation: 0
    });
    
    const [showAttestationModal, setShowAttestationModal] = useState(false);
    const [selectedInscrit, setSelectedInscrit] = useState(null);
    const [signatureData, setSignatureData] = useState({
        logo_base64: null,
        signature_titre: '',
        signature_nom: '',
        signature_prenom: ''
    });
    const [logoPreview, setLogoPreview] = useState(null);

    const [showRecuModal, setShowRecuModal] = useState(false);
    const [recuData, setRecuData] = useState({
        logo_base64: null,
        signature_titre: '',
        signature_nom: '',
        signature_prenom: '',
        montant_recu: 0,
        motif: ''
    });
    const [logoRecuPreview, setLogoRecuPreview] = useState(null);

    const [showPaiementModal, setShowPaiementModal] = useState(false);
    const [paiementData, setPaiementData] = useState({
        id_inscrit: null,
        type_paiement: 'formation',
        montant: 0,
        commentaire: ''
    });
    const [paiementsList, setPaiementsList] = useState([]);

    const API_BASE = config.apiBase;
    const getToken = () => localStorage.getItem('token');

    // ============================================
    // REQUÊTES API
    // ============================================
    const fetchFormations = async () => {
        setLoading(true);
        try {
            const token = getToken();
            const res = await fetch(`${API_BASE}/enseignant/formations.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setFormations(data.formations);
            }
        } catch (error) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    const fetchInscrits = async (id_formation) => {
        try {
            const token = getToken();
            const res = await fetch(`${API_BASE}/enseignant/inscrits.php?id_formation=${id_formation}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setInscrits(data.inscrits);
            }
        } catch (error) {
            toast.error('Erreur de chargement des inscrits');
        }
    };

    const fetchPaiements = async (id_inscrit) => {
        try {
            const token = getToken();
            const res = await fetch(`${API_BASE}/enseignant/paiements.php?id_inscrit=${id_inscrit}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setPaiementsList(data.paiements);
            }
        } catch (error) {
            toast.error('Erreur de chargement des paiements');
        }
    };

    useEffect(() => {
        fetchFormations();
    }, []);

    // ============================================
    // GESTION DES FORMATIONS
    // ============================================
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nom.trim()) {
            toast.error('Le nom de la formation est requis');
            return;
        }

        setLoading(true);
        try {
            const token = getToken();
            const payload = { ...formData };
            if (editingId) payload.id_formation = editingId;

            const res = await fetch(`${API_BASE}/enseignant/formations.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                toast.success(editingId ? 'Formation modifiée' : 'Formation créée');
                setShowForm(false);
                setEditingId(null);
                setFormData({ nom: '', description: '', photo: '', frais_inscription: '', frais_formation: '', duree_mois: 3 });
                fetchFormations();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setLoading(false);
        }
    };

    const supprimerFormation = async (id) => {
        if (!window.confirm('⚠️ Supprimer cette formation et tous ses inscrits ?')) return;
        try {
            const token = getToken();
            const res = await fetch(`${API_BASE}/enseignant/formations.php`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ id_formation: id })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Formation supprimée');
                fetchFormations();
            }
        } catch (error) {
            toast.error('Erreur');
        }
    };

    // ============================================
    // GESTION DES INSCRITS
    // ============================================
    const handleInscrire = async (e) => {
        e.preventDefault();
        if (!selectedFormation) return;

        setLoading(true);
        try {
            const token = getToken();
            const payload = {
                ...inscritData,
                id_formation: selectedFormation.id_formation
            };

            const res = await fetch(`${API_BASE}/enseignant/inscrits.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Inscription enregistrée');
                setInscritData({
                    nom: '',
                    prenom: '',
                    date_naissance: '',
                    lieu_naissance: '',
                    sexe: 'M',
                    telephone: '',
                    email: '',
                    montant_inscription: 0,
                    montant_formation: 0
                });
                fetchInscrits(selectedFormation.id_formation);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setLoading(false);
        }
    };

    const voirInscrits = async (formation) => {
        setSelectedFormation(formation);
        setShowInscrits(true);
        await fetchInscrits(formation.id_formation);
    };

    // ============================================
    // GESTION DES PAIEMENTS
    // ============================================
    const ouvrirPaiementModal = async (inscrit) => {
        setSelectedInscrit(inscrit);
        setPaiementData({
            id_inscrit: inscrit.id_inscrit,
            type_paiement: 'formation',
            montant: 0,
            commentaire: ''
        });
        await fetchPaiements(inscrit.id_inscrit);
        setShowPaiementModal(true);
    };

    const ajouterPaiement = async (e) => {
        e.preventDefault();
        if (paiementData.montant <= 0) {
            toast.error('Le montant doit être supérieur à 0');
            return;
        }

        setLoading(true);
        try {
            const token = getToken();
            const payload = {
                id_inscrit: paiementData.id_inscrit,
                type_paiement: paiementData.type_paiement,
                montant: paiementData.montant,
                commentaire: paiementData.commentaire
            };

            const res = await fetch(`${API_BASE}/enseignant/paiements.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Paiement ajouté avec succès');
                setPaiementData({
                    ...paiementData,
                    montant: 0,
                    commentaire: ''
                });
                await fetchPaiements(paiementData.id_inscrit);
                await fetchInscrits(selectedFormation?.id_formation);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur lors de l\'ajout du paiement');
        } finally {
            setLoading(false);
        }
    };

    const supprimerPaiement = async (id_paiement) => {
        if (!window.confirm('Supprimer ce paiement ?')) return;
        try {
            const token = getToken();
            const res = await fetch(`${API_BASE}/enseignant/paiements.php`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ id_paiement })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Paiement supprimé');
                await fetchPaiements(paiementData.id_inscrit);
                await fetchInscrits(selectedFormation?.id_formation);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        }
    };

    // ============================================
    // GESTION DES ATTESTATIONS ET REÇUS
    // ============================================
    const genererAttestation = (inscrit) => {
        setSelectedInscrit(inscrit);
        setShowAttestationModal(true);
    };

    const genererRecu = (inscrit) => {
        setSelectedInscrit(inscrit);
        setRecuData({
            ...recuData,
            montant_recu: inscrit.montant_inscription_paye + inscrit.montant_formation_paye,
            motif: `Paiement formation ${selectedFormation?.nom || ''}`
        });
        setShowRecuModal(true);
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setSignatureData({ ...signatureData, logo_base64: reader.result });
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLogoRecuChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setRecuData({ ...recuData, logo_base64: reader.result });
                setLogoRecuPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const imprimerAttestation = async () => {
        if (!selectedInscrit) return;

        try {
            const token = getToken();
            const payload = {
                id_inscrit: selectedInscrit.id_inscrit,
                logo_base64: signatureData.logo_base64,
                signature_titre: signatureData.signature_titre,
                signature_nom: signatureData.signature_nom,
                signature_prenom: signatureData.signature_prenom
            };

            const res = await fetch(`${API_BASE}/enseignant/generer_attestation.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                const win = window.open('', '_blank');
                win.document.write(data.html);
                win.document.close();
                win.print();
                setShowAttestationModal(false);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const imprimerRecu = async () => {
        if (!selectedInscrit) return;

        try {
            const token = getToken();
            const payload = {
                id_inscrit: selectedInscrit.id_inscrit,
                logo_base64: recuData.logo_base64,
                signature_titre: recuData.signature_titre,
                signature_nom: recuData.signature_nom,
                signature_prenom: recuData.signature_prenom,
                montant_recu: recuData.montant_recu,
                motif: recuData.motif
            };

            const res = await fetch(`${API_BASE}/enseignant/generer_recu.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                const win = window.open('', '_blank');
                win.document.write(data.html);
                win.document.close();
                win.print();
                setShowRecuModal(false);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        }
    };

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* ============================================ */}
            {/* EN-TÊTE PRINCIPAL */}
            {/* ============================================ */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-6 mb-8 text-white shadow-lg">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="text-3xl font-bold">🎓 Gestion des formations</h1>
                        <p className="text-blue-100 mt-1">Créez et gérez vos formations professionnelles</p>
                    </div>
                    <button
                        onClick={() => { setShowForm(!showForm); setEditingId(null); setFormData({ nom: '', description: '', photo: '', frais_inscription: '', frais_formation: '', duree_mois: 3 }); }}
                        className="bg-white text-blue-700 px-6 py-3 rounded-xl hover:bg-blue-50 transition shadow-lg flex items-center gap-2 font-semibold"
                    >
                        <span className="text-xl">+</span> Nouvelle formation
                    </button>
                </div>
            </div>

            {/* ============================================ */}
            {/* FORMULAIRE DE CRÉATION */}
            {/* ============================================ */}
            {showForm && (
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-gray-100">
                    <h2 className="text-xl font-bold mb-4 text-gray-800">{editingId ? '✏️ Modifier la formation' : '📝 Nouvelle formation'}</h2>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la formation *</label>
                            <input
                                type="text"
                                value={formData.nom}
                                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                                className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Durée (mois)</label>
                            <input
                                type="number"
                                value={formData.duree_mois}
                                onChange={(e) => setFormData({ ...formData, duree_mois: parseInt(e.target.value) || 3 })}
                                className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                min="1"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Frais d'inscription (FCFA)</label>
                            <input
                                type="number"
                                value={formData.frais_inscription}
                                onChange={(e) => setFormData({ ...formData, frais_inscription: e.target.value })}
                                className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Frais de formation (FCFA)</label>
                            <input
                                type="number"
                                value={formData.frais_formation}
                                onChange={(e) => setFormData({ ...formData, frais_formation: e.target.value })}
                                className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                rows="3"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">URL de la photo publicitaire</label>
                            <input
                                type="text"
                                value={formData.photo}
                                onChange={(e) => setFormData({ ...formData, photo: e.target.value })}
                                className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="https://exemple.com/photo.jpg"
                            />
                        </div>
                        <div className="md:col-span-2 flex gap-3">
                            <button type="submit" disabled={loading} className="bg-blue-600 text-white px-6 py-2 rounded-xl hover:bg-blue-700 transition disabled:opacity-50">
                                {loading ? '⏳ Enregistrement...' : editingId ? '💾 Modifier' : '➕ Créer'}
                            </button>
                            <button type="button" onClick={() => setShowForm(false)} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-xl hover:bg-gray-300 transition">
                                Annuler
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* ============================================ */}
            {/* LISTE DES FORMATIONS */}
            {/* ============================================ */}
            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
                </div>
            ) : formations.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                    <div className="text-6xl mb-4">🎓</div>
                    <p className="text-gray-500 text-lg">Aucune formation créée</p>
                    <p className="text-sm text-gray-400">Cliquez sur "Nouvelle formation" pour commencer</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {formations.map((f) => (
                        <div key={f.id_formation} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:transform hover:-translate-y-1">
                            {f.photo && (
                                <img src={f.photo} alt={f.nom} className="w-full h-48 object-cover" />
                            )}
                            <div className="p-5">
                                <h3 className="font-bold text-xl text-gray-800 mb-1">{f.nom}</h3>
                                <p className="text-sm text-gray-500 line-clamp-2 mb-3">{f.description || 'Aucune description'}</p>
                                <div className="flex flex-wrap gap-2 mb-3">
                                    <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full">📅 {f.duree_mois} mois</span>
                                    <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">💰 {f.frais_inscription} F</span>
                                    <span className="text-xs bg-purple-100 text-purple-700 px-3 py-1 rounded-full">📚 {f.frais_formation} F</span>
                                    <span className="text-xs bg-orange-100 text-orange-700 px-3 py-1 rounded-full">👥 {f.nb_inscrits || 0} inscrits</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={() => voirInscrits(f)}
                                        className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
                                    >
                                        👥 Voir les inscrits
                                    </button>
                                    <button
                                        onClick={() => { setEditingId(f.id_formation); setFormData(f); setShowForm(true); }}
                                        className="bg-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-300 transition text-sm"
                                    >
                                        ✏️
                                    </button>
                                    <button
                                        onClick={() => supprimerFormation(f.id_formation)}
                                        className="bg-red-50 text-red-600 px-3 py-2 rounded-lg hover:bg-red-100 transition text-sm"
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ============================================ */}
            {/* MODAL - INSCRITS */}
            {/* ============================================ */}
            {showInscrits && selectedFormation && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
                        {/* En-tête de la modale */}
                        <div className="flex items-center justify-between mb-4 border-b pb-4">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">
                                    👥 Inscrits
                                </h2>
                                <p className="text-sm text-blue-600 font-medium">
                                    {selectedFormation.nom}
                                </p>
                            </div>
                            <button 
                                onClick={() => { setShowInscrits(false); setSelectedFormation(null); }} 
                                className="text-gray-400 hover:text-gray-600 text-2xl transition"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Formulaire d'inscription */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-6 border border-blue-200">
                            <h3 className="font-semibold mb-3 text-gray-700 flex items-center gap-2">
                                <span className="text-green-600 text-xl">➕</span> Ajouter un inscrit
                            </h3>
                            <form onSubmit={handleInscrire} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <input type="text" placeholder="Nom *" value={inscritData.nom} onChange={(e) => setInscritData({ ...inscritData, nom: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 transition" required />
                                <input type="text" placeholder="Prénom *" value={inscritData.prenom} onChange={(e) => setInscritData({ ...inscritData, prenom: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 transition" required />
                                <input type="date" value={inscritData.date_naissance} onChange={(e) => setInscritData({ ...inscritData, date_naissance: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 transition" required />
                                <input type="text" placeholder="Lieu naissance *" value={inscritData.lieu_naissance} onChange={(e) => setInscritData({ ...inscritData, lieu_naissance: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 transition" required />
                                <select value={inscritData.sexe} onChange={(e) => setInscritData({ ...inscritData, sexe: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 transition">
                                    <option value="M">👨 Masculin</option>
                                    <option value="F">👩 Féminin</option>
                                </select>
                                <input type="text" placeholder="Téléphone" value={inscritData.telephone} onChange={(e) => setInscritData({ ...inscritData, telephone: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 transition" />
                                <input type="email" placeholder="Email" value={inscritData.email} onChange={(e) => setInscritData({ ...inscritData, email: e.target.value })} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 transition" />
                                <input type="number" placeholder="Montant inscription" value={inscritData.montant_inscription} onChange={(e) => setInscritData({ ...inscritData, montant_inscription: parseFloat(e.target.value) || 0 })} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 transition" />
                                <input type="number" placeholder="Montant formation" value={inscritData.montant_formation} onChange={(e) => setInscritData({ ...inscritData, montant_formation: parseFloat(e.target.value) || 0 })} className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 transition" />
                                <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition font-semibold">
                                    ➕ Ajouter
                                </button>
                            </form>
                        </div>

                        {/* Liste des inscrits */}
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-medium">Nom</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium">Prénom</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium">Sexe</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium">Payé</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium">%</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium">Fin</th>
                                        <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {inscrits.map((i, index) => (
                                        <tr key={i.id_inscrit} className={`hover:bg-blue-50 transition ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                            <td className="px-4 py-2 text-sm font-medium">{i.nom}</td>
                                            <td className="px-4 py-2 text-sm">{i.prenom}</td>
                                            <td className="px-4 py-2 text-sm">{i.sexe === 'M' ? '♂️' : '♀️'}</td>
                                            <td className="px-4 py-2 text-sm font-medium">{i.montant_inscription_paye + i.montant_formation_paye} F</td>
                                            <td className="px-4 py-2 text-sm">
                                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${i.pourcentage_paye >= 100 ? 'bg-green-100 text-green-700' : i.pourcentage_paye >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                                    {i.pourcentage_paye}%
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-sm">{i.date_fin ? new Date(i.date_fin).toLocaleDateString() : '-'}</td>
                                            <td className="px-4 py-2 text-center">
                                                <div className="flex flex-wrap justify-center gap-1">
                                                    <button
                                                        onClick={() => genererAttestation(i)}
                                                        className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition text-sm"
                                                        title="Générer l'attestation"
                                                    >
                                                        📜
                                                    </button>
                                                    <button
                                                        onClick={() => genererRecu(i)}
                                                        className="bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition text-sm"
                                                        title="Générer le reçu"
                                                    >
                                                        💰
                                                    </button>
                                                    <button
                                                        onClick={() => ouvrirPaiementModal(i)}
                                                        className="bg-purple-600 text-white px-3 py-1 rounded-lg hover:bg-purple-700 transition text-sm"
                                                        title="Gérer les paiements"
                                                    >
                                                        💳
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* MODAL - ATTESTATION */}
            {/* ============================================ */}
            {showAttestationModal && selectedInscrit && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
                        <div className="flex items-center justify-between mb-4 border-b pb-3">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                📜 Attestation
                            </h2>
                            <button 
                                onClick={() => { setShowAttestationModal(false); setSelectedInscrit(null); }} 
                                className="text-gray-400 hover:text-gray-600 text-2xl transition"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                                <p className="text-sm text-gray-600">
                                    <span className="font-semibold">Apprenant :</span> {selectedInscrit.prenom} {selectedInscrit.nom}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">🖼️ Logo</label>
                                <input type="file" accept="image/*" onChange={handleLogoChange} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 transition" />
                                {logoPreview && (
                                    <div className="mt-2 flex items-center gap-3">
                                        <img src={logoPreview} alt="Logo" className="h-12 object-contain border rounded-lg p-1" />
                                        <span className="text-xs text-green-600">✓ Logo chargé</span>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Titre du signataire</label>
                                    <input type="text" value={signatureData.signature_titre} onChange={(e) => setSignatureData({ ...signatureData, signature_titre: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 transition" placeholder="Directeur Général" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                                    <input type="text" value={signatureData.signature_nom} onChange={(e) => setSignatureData({ ...signatureData, signature_nom: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 transition" />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                                    <input type="text" value={signatureData.signature_prenom} onChange={(e) => setSignatureData({ ...signatureData, signature_prenom: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 transition" />
                                </div>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button onClick={imprimerAttestation} className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-semibold flex items-center justify-center gap-2">
                                    📄 Générer
                                </button>
                                <button onClick={() => { setShowAttestationModal(false); setSelectedInscrit(null); }} className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition">
                                    Annuler
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* MODAL - REÇU */}
            {/* ============================================ */}
            {showRecuModal && selectedInscrit && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
                        <div className="flex items-center justify-between mb-4 border-b pb-3">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                💰 Reçu de paiement
                            </h2>
                            <button 
                                onClick={() => { setShowRecuModal(false); setSelectedInscrit(null); }} 
                                className="text-gray-400 hover:text-gray-600 text-2xl transition"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                                <p className="text-sm text-gray-600">
                                    <span className="font-semibold">Apprenant :</span> {selectedInscrit.prenom} {selectedInscrit.nom}
                                </p>
                                <p className="text-sm text-gray-600">
                                    <span className="font-semibold">Total payé :</span> {selectedInscrit.montant_inscription_paye + selectedInscrit.montant_formation_paye} F
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">🖼️ Logo</label>
                                <input type="file" accept="image/*" onChange={handleLogoRecuChange} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 transition" />
                                {logoRecuPreview && (
                                    <div className="mt-2 flex items-center gap-3">
                                        <img src={logoRecuPreview} alt="Logo" className="h-12 object-contain border rounded-lg p-1" />
                                        <span className="text-xs text-green-600">✓ Logo chargé</span>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Montant reçu (FCFA)</label>
                                <input type="number" value={recuData.montant_recu} onChange={(e) => setRecuData({ ...recuData, montant_recu: parseFloat(e.target.value) || 0 })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 transition" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Motif du paiement</label>
                                <input type="text" value={recuData.motif} onChange={(e) => setRecuData({ ...recuData, motif: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 transition" placeholder="Frais de formation" />
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Titre du signataire</label>
                                    <input type="text" value={recuData.signature_titre} onChange={(e) => setRecuData({ ...recuData, signature_titre: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 transition" placeholder="Directeur Général" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                                    <input type="text" value={recuData.signature_nom} onChange={(e) => setRecuData({ ...recuData, signature_nom: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 transition" />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                                    <input type="text" value={recuData.signature_prenom} onChange={(e) => setRecuData({ ...recuData, signature_prenom: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 transition" />
                                </div>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button onClick={imprimerRecu} className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition font-semibold flex items-center justify-center gap-2">
                                    💳 Générer
                                </button>
                                <button onClick={() => { setShowRecuModal(false); setSelectedInscrit(null); }} className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition">
                                    Annuler
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* MODAL - PAIEMENTS */}
            {/* ============================================ */}
            {showPaiementModal && selectedInscrit && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
                        <div className="flex items-center justify-between mb-4 border-b pb-3">
                            <div>
                                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                    💳 Gestion des paiements
                                </h2>
                                <p className="text-sm text-purple-600 font-medium">
                                    {selectedInscrit.prenom} {selectedInscrit.nom}
                                </p>
                            </div>
                            <button 
                                onClick={() => { setShowPaiementModal(false); setSelectedInscrit(null); }} 
                                className="text-gray-400 hover:text-gray-600 text-2xl transition"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Formulaire d'ajout */}
                        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 mb-6 border border-purple-200">
                            <h3 className="font-semibold mb-3 text-gray-700 flex items-center gap-2">
                                <span className="text-purple-600 text-xl">➕</span> Ajouter un paiement
                            </h3>
                            <form onSubmit={ajouterPaiement} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                    <select 
                                        value={paiementData.type_paiement} 
                                        onChange={(e) => setPaiementData({ ...paiementData, type_paiement: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 transition"
                                    >
                                        <option value="inscription">📝 Inscription</option>
                                        <option value="formation">📚 Formation</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Montant (FCFA)</label>
                                    <input 
                                        type="number" 
                                        value={paiementData.montant} 
                                        onChange={(e) => setPaiementData({ ...paiementData, montant: parseFloat(e.target.value) || 0 })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 transition"
                                        required
                                        min="0"
                                        step="100"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Commentaire</label>
                                    <input 
                                        type="text" 
                                        value={paiementData.commentaire} 
                                        onChange={(e) => setPaiementData({ ...paiementData, commentaire: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 transition"
                                        placeholder="Ex: Paiement 1ère tranche"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <button type="submit" className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition font-semibold flex items-center gap-2">
                                        💳 Ajouter le paiement
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Résumé des paiements */}
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
                                <p className="text-xs text-gray-500">Total payé</p>
                                <p className="text-lg font-bold text-green-600">
                                    {paiementsList.reduce((sum, p) => sum + p.montant, 0).toLocaleString()} F
                                </p>
                            </div>
                            <div className="bg-yellow-50 rounded-lg p-3 text-center border border-yellow-200">
                                <p className="text-xs text-gray-500">Frais inscription</p>
                                <p className="text-lg font-bold text-blue-600">
                                    {selectedInscrit.frais_inscription?.toLocaleString() || 0} F
                                </p>
                            </div>
                            <div className="bg-red-50 rounded-lg p-3 text-center border border-red-200">
                                <p className="text-xs text-gray-500">Reste à payer</p>
                                <p className="text-lg font-bold text-red-600">
                                    {((selectedInscrit.frais_inscription || 0) + (selectedInscrit.frais_formation || 0) - paiementsList.reduce((sum, p) => sum + p.montant, 0)).toLocaleString()} F
                                </p>
                            </div>
                        </div>

                        {/* Liste des paiements */}
                        <div className="overflow-x-auto">
                            <h4 className="font-semibold text-gray-700 mb-2">📋 Historique</h4>
                            <table className="w-full">
                                <thead className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-t-lg">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-sm font-medium">Date</th>
                                        <th className="px-4 py-2 text-left text-sm font-medium">Type</th>
                                        <th className="px-4 py-2 text-right text-sm font-medium">Montant</th>
                                        <th className="px-4 py-2 text-left text-sm font-medium">Commentaire</th>
                                        <th className="px-4 py-2 text-center text-sm font-medium">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {paiementsList.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="px-4 py-4 text-center text-gray-500">
                                                Aucun paiement enregistré
                                            </td>
                                        </tr>
                                    ) : (
                                        paiementsList.map((p) => (
                                            <tr key={p.id_paiement} className="hover:bg-purple-50 transition">
                                                <td className="px-4 py-2 text-sm">{new Date(p.date_paiement).toLocaleDateString()}</td>
                                                <td className="px-4 py-2 text-sm">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                                        p.type_paiement === 'inscription' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                                    }`}>
                                                        {p.type_paiement === 'inscription' ? '📝 Inscription' : '📚 Formation'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 text-sm text-right font-bold text-green-600">
                                                    {p.montant.toLocaleString()} F
                                                </td>
                                                <td className="px-4 py-2 text-sm">{p.commentaire || '-'}</td>
                                                <td className="px-4 py-2 text-center">
                                                    <button
                                                        onClick={() => supprimerPaiement(p.id_paiement)}
                                                        className="bg-red-50 text-red-600 px-3 py-1 rounded-lg hover:bg-red-100 transition text-sm"
                                                        title="Supprimer"
                                                    >
                                                        🗑️
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                <tfoot className="bg-gray-50 font-bold">
                                    <tr>
                                        <td colSpan="2" className="px-4 py-2 text-right">Total :</td>
                                        <td className="px-4 py-2 text-right text-green-600">
                                            {paiementsList.reduce((sum, p) => sum + p.montant, 0).toLocaleString()} F
                                        </td>
                                        <td colSpan="2"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}